import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { useState, useMemo } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  Typography,
  Button,
  InputAdornment,
  CircularProgress,
  Collapse,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useAuth } from "../../../auth";
import { useTranslation } from "react-i18next";
import { usePermissions } from "../../../hooks/usePermissions";
import ListLayout from "../../../components/Layout/ListLayout";
import {
  AuditApi,
  type AuditSearchParams,
  type AuditLogEntry,
} from "../../../api/auditApi";

const auditSearchSchema = z.object({
  search: z.string().optional(),
  action: z.string().optional(),
  resourceType: z.string().optional(),
  resourceId: z.string().optional(),
  sourceService: z.string().optional(),
  status: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  page: z.number().optional(),
  size: z.number().optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
});

export const Route = createFileRoute("/__auth/admin/audit-logs")({
  component: AuditLogsComponent,
  errorComponent: AuditLogsErrorComponent,
  validateSearch: (search) => auditSearchSchema.parse(search),
  beforeLoad: ({ context }) => {
    if (context.auth.isLoading) return;
    if (context.auth.user && !context.auth.user.isAdmin) {
      throw redirect({
        to: "/dashboard",
      });
    }
  },
  staticData: {
    title: "routes.auditLogs",
  },
});

function AuditLogsErrorComponent({ reset }: { error: Error; reset?: () => void }) {
  const { t, i18n } = useTranslation();

  return (
    <Box sx={{ p: 4, maxWidth: 720, mx: "auto" }}>
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => (reset ? reset() : window.location.reload())}
            sx={{ textTransform: "none" }}
          >
            {t("common.refresh")}
          </Button>
        }
      >
        <AlertTitle>{t("admin.auditLogs.title")}</AlertTitle>
        {t("admin.auditLogs.errorLoading")}
      </Alert>
    </Box>
  );
}

const ACTIONS = ["CREATE", "UPDATE", "DELETE", "ADD_USER", "REMOVE_USER", "EXECUTE_TASK", "COMPLETE_TASK"];

const RESOURCE_TYPES = [
  "ACCESS_PROFILE",
  "CLASSIFICATION",
  "DATA_LIST",
  "DOCUMENT",
  "FOLDER",
  "GROUP",
  "JOB",
  "OCCUPATION",
  "ORGANIZATION",
  "PROCESS_DEFINITION",
  "PROCESS_TASK",
  "STOCKING_AREA",
  "TEMPLATE",
  "USER",
  "USER_PASSWORD",
  "WORKSPACE",
];

const SOURCE_SERVICES = ["smartdocsbpm", "smartdoczdocumentos"];

const STATUSES = ["SUCCESS", "FAILURE"];

function getActionChipColor(
  action: string
): "success" | "info" | "error" | "warning" | "default" {
  switch (action) {
    case "CREATE":
      return "success";
    case "UPDATE":
      return "info";
    case "DELETE":
      return "error";
    case "ADD_USER":
      return "success";
    case "REMOVE_USER":
      return "warning";
    default:
      return "default";
  }
}

function getStatusChipColor(
  status: string
): "success" | "error" | "warning" | "default" {
  switch (status) {
    case "SUCCESS":
      return "success";
    case "FAILURE":
      return "error";
    default:
      return "default";
  }
}

function normalizeResponse(raw: unknown): {
  rows: AuditLogEntry[];
  totalElements: number;
} {
  const payload = (raw as any)?.content ? raw : ((raw as any)?.data ?? raw);
  const content = (payload as any)?.content;

  const rows: AuditLogEntry[] = Array.isArray(content)
    ? content
    : Array.isArray(payload)
      ? (payload as AuditLogEntry[])
      : [];

  const total = (payload as any)?.totalElements;

  return {
    rows,
    totalElements: typeof total === "number" && total >= 0 ? total : rows.length,
  };
}

function isBackendUnavailable(error: unknown): boolean {
  if (!error) return false;
  const status = (error as any)?.response?.status ?? (error as any)?.status;
  if (typeof status === "number") {
    return status === 0 || status === 502 || status === 503 || status === 504;
  }
  return true;
}

function formatDateTime(
  dateStr: string | null | undefined,
  locale: string
): string {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

const initialFilters: AuditSearchParams = {
  search: "",
  action: "",
  resourceType: "",
  sourceService: "",
  status: "",
  dateFrom: "",
  dateTo: "",
  page: 0,
  size: 20,
  sortOrder: "desc",
};

function AuditLogsComponent() {
  const { t, i18n } = useTranslation();
  const search = Route.useSearch();
  const { currentTenant } = useAuth();
  const { isAdmin, isLoading: isLoadingPermissions } = usePermissions();

  const effectiveTenant = currentTenant || "default-domain";

  const traduzirValor = (grupo: string, valor: string | null | undefined) =>
    valor ? t(`admin.auditLogs.${grupo}.${valor}`, { defaultValue: valor }) : "-";

  const [filters, setFilters] = useState<AuditSearchParams>({
    ...initialFilters,
    ...search,
  });
  const [filtersOpen, setFiltersOpen] = useState(true);

  const queryParams = useMemo(() => {
    const params: AuditSearchParams = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        (params as Record<string, unknown>)[key] = value;
      }
    });
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["audit-logs", effectiveTenant, queryParams],
    queryFn: () => AuditApi.search(effectiveTenant, queryParams),
    enabled: isAdmin,
    placeholderData: keepPreviousData,
    retry: 1,
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  const handleFilterChange = (field: keyof AuditSearchParams, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFilters((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  if (isLoadingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4, mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 4, maxWidth: 720, mx: "auto" }}>
        <Alert severity="warning">
          <AlertTitle>{t("admin.auditLogs.title")}</AlertTitle>
          {t("admin.auditLogs.adminOnly")}
        </Alert>
      </Box>
    );
  }

  const { rows, totalElements } = normalizeResponse(data);
  const hasActiveFilters = Object.entries(filters).some(
    ([key, value]) =>
      key !== "page" &&
      key !== "size" &&
      key !== "sortOrder" &&
      value !== "" &&
      value !== undefined
  );

  return (
    <ListLayout
      title={t("admin.auditLogs.title")}
      subtitle={t("admin.auditLogs.subtitle")}
      actions={
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          disabled={isFetching}
          onClick={() => void refetch()}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          {t("admin.auditLogs.refresh")}
        </Button>
      }
      isLoading={isLoading}
      isEmpty={false}
    >
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[2],
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 1.5,
            cursor: "pointer",
          }}
          onClick={() => setFiltersOpen(!filtersOpen)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FilterListIcon color="action" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t("admin.auditLogs.filters")}
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={t("admin.auditLogs.activeFilters")}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton size="small">
            {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ px: 3, pb: 3 }}>
            <TextField
              fullWidth
              size="small"
              placeholder={t("admin.auditLogs.searchPlaceholder")}
              value={filters.search || ""}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr 1fr",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                select
                size="small"
                label={t("admin.auditLogs.action")}
                value={filters.action || ""}
                onChange={(e) => handleFilterChange("action", e.target.value)}
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                {ACTIONS.map((action) => (
                  <MenuItem key={action} value={action}>
                    {traduzirValor("actions", action)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={t("admin.auditLogs.resourceType")}
                value={filters.resourceType || ""}
                onChange={(e) =>
                  handleFilterChange("resourceType", e.target.value)
                }
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                {RESOURCE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {traduzirValor("resourceTypes", type)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={t("admin.auditLogs.sourceService")}
                value={filters.sourceService || ""}
                onChange={(e) =>
                  handleFilterChange("sourceService", e.target.value)
                }
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                {SOURCE_SERVICES.map((svc) => (
                  <MenuItem key={svc} value={svc}>
                    {traduzirValor("sourceServices", svc)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label={t("admin.auditLogs.status")}
                value={filters.status || ""}
                onChange={(e) => handleFilterChange("status", e.target.value)}
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                {STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {traduzirValor("statuses", status)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr auto",
                },
                gap: 2,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                type="datetime-local"
                label={t("admin.auditLogs.dateFrom")}
                value={filters.dateFrom || ""}
                onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                size="small"
                type="datetime-local"
                label={t("admin.auditLogs.dateTo")}
                value={filters.dateTo || ""}
                onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                InputLabelProps={{ shrink: true }}
              />

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearFilters}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  {t("admin.auditLogs.clearFilters")}
                </Button>
              )}
            </Box>
          </Box>
        </Collapse>
      </Paper>

      {error ? (
        <Alert
          severity={isBackendUnavailable(error) ? "warning" : "error"}
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              disabled={isFetching}
              onClick={() => void refetch()}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              {t("common.refresh")}
            </Button>
          }
        >
          {isBackendUnavailable(error)
            ? t("admin.auditLogs.serviceUnavailable")
            : t("admin.auditLogs.errorLoading")}
        </Alert>
      ) : null}

      {!error && !isLoading && rows.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t("admin.auditLogs.noResults")}
        </Alert>
      )}

      {rows.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            boxShadow: (theme) => theme.shadows[2],
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("admin.auditLogs.dateTime")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("admin.auditLogs.user")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("admin.auditLogs.action")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("admin.auditLogs.resourceType")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("admin.auditLogs.resource")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("admin.auditLogs.sourceService")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("admin.auditLogs.status")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {t("admin.auditLogs.description")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    key={`${row.createdAt}-${row.userId}-${index}`}
                    hover
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDateTime(row.createdAt, i18n.language)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {row.userName || row.userId || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={traduzirValor("actions", row.action)}
                        size="small"
                        color={getActionChipColor(row.action)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>
                        {traduzirValor("resourceTypes", row.resourceType)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ maxWidth: 200 }}
                        title={
                          row.resourceName
                            ? `${row.resourceName} (${row.resourceId})`
                            : row.resourceId
                        }
                      >
                        {row.resourceName || row.resourceId || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={traduzirValor("sourceServices", row.sourceService)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: "0.75rem" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={traduzirValor("statuses", row.status)}
                        size="small"
                        color={getStatusChipColor(row.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ maxWidth: 250 }}
                        title={row.description}
                      >
                        {row.description || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalElements}
            page={Math.min(
              filters.page || 0,
              Math.max(0, Math.ceil(totalElements / (filters.size || 20)) - 1)
            )}
            onPageChange={handleChangePage}
            rowsPerPage={filters.size || 20}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            labelRowsPerPage={t("common.rowsPerPage")}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${t("common.of")} ${count !== -1 ? count : `${t("common.moreThan")} ${to}`}`
            }
          />
        </Paper>
      )}
    </ListLayout>
  );
}
